import { Circle } from '@react-three/drei'
import { forwardRef, useState, useImperativeHandle } from 'react'
import { Vector2 } from 'three'
import { CURSOR_Z } from '~/components/DrawingCanvas/constants/zIndices'
import { useProject } from '~/components/DrawingCanvas/hooks'
import { ToolManagerRef } from '~/components/DrawingCanvas/ToolManager'
import { circlePoints } from '~/components/DrawingCanvas/util/circlePoints'
import { SnapLine } from '~/components/DrawingCanvas/util/snaplineVisuals'
import { modelToUI } from '~/components/DrawingCanvas/util/units'
import { WallMesh } from '~/components/DrawingCanvas/Walls/components/WallMesh'
import { MINIMUM_THRESHOLD } from '~/components/DrawingCanvas/Walls/constants'
import { useMakeWall } from '~/components/DrawingCanvas/Walls/hooks/useMakeWall'
import { useSnap } from '~/components/DrawingCanvas/Walls/hooks/useSnap'
import { computeEdges } from '~/components/DrawingCanvas/Walls/util'
import { DEFAULT_WALL_SIZES } from '~/config/wallSizes'
import { useAppSelector } from '~/store/hooks'

export const WallEllipseTool = forwardRef<ToolManagerRef, object>(function WallEllipseTool(_props, ref) {
  const layerKey = useAppSelector(state => state.layers.currentLayer)
  const config =
    layerKey in DEFAULT_WALL_SIZES
      ? DEFAULT_WALL_SIZES[layerKey as keyof typeof DEFAULT_WALL_SIZES]
      : DEFAULT_WALL_SIZES.EXTERIOR_WALLS
  const thickness = modelToUI(config.thickness)
  const height = modelToUI(config.height)
  const makeWall = useMakeWall(layerKey)

  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const [pointOne, setPointOne] = useState<[x: number, y: number] | undefined>(undefined)

  const [snapPoint, current] = useSnap(x, y)

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          setPoint([point.x, point.y])
        }
        return true
      },
      onMouseDown(event) {
        if (pointOne === undefined && event.button === 0) {
          setPointOne([current.x, current.y])
        }
        return true
      },
      onMouseUp(event) {
        if (pointOne === undefined || event.button !== 0) {
          return true
        }

        const isTooSmall =
          new Vector2(current.x, current.y).distanceToSquared({ x: pointOne[0], y: pointOne[1] }) < MINIMUM_THRESHOLD
        if (isTooSmall) {
          setPointOne(undefined)
          return true
        }

        const points = circlePoints(current.x, current.y, pointOne[0], pointOne[1])
        points.push(points[0])

        makeWall(points)

        setPointOne(undefined)
        return true
      },
    }),
    [project, current.x, current.y, pointOne, makeWall]
  )

  if (pointOne === undefined || (pointOne[0] === current.x && pointOne[1] === current.y)) {
    return (
      <>
        <Circle position={[current.x, current.y, CURSOR_Z]} args={[2, 20]} material-color="pink" />
        {snapPoint && <SnapLine snapPoint={snapPoint} zOverride={300} />}
      </>
    )
  }

  const distSqd = (pointOne[0] - current.x) ** 2 + (pointOne[1] - current.y) ** 2
  const isOverlapping = pointOne[0] === current.x || pointOne[1] === current.y
  if (distSqd < thickness || isOverlapping) {
    return (
      <>
        <Circle position={[current.x, current.y, CURSOR_Z]} args={[2, 20]} material-color="pink" />
        {snapPoint && <SnapLine snapPoint={snapPoint} zOverride={300} />}
      </>
    )
  }

  const points = circlePoints(current.x, current.y, pointOne[0], pointOne[1])
  points.push(points[0])

  return (
    <>
      <Circle position={[current.x, current.y, CURSOR_Z]} args={[2, 20]} material-color="pink" />
      {snapPoint && <SnapLine snapPoint={snapPoint} zOverride={300} />}
      <WallMesh edges={computeEdges(points, thickness)} height={height} />
    </>
  )
})
